import { useMutation } from '@apollo/client';
import React, { useState } from 'react';
import { v4 } from 'uuid';

import { useApiClient } from '@eluve/api-client-provider';
import { InlineFeedback } from '@eluve/blocks';
import { useToast } from '@eluve/components';
import { useIsAppointmentReadonly } from '@eluve/frontend-appointment-hooks';
import { FeedbackTypeEnum } from '@eluve/graphql-types';
import { graphql } from '@eluve/graphql.tada';
import { useAssignedTenantIdFromParams } from '@eluve/session-helpers';

const feedbackFragment = graphql(`
  fragment FeedbackFragment on SummaryFeedback @_unmask {
    __typename
    id
    type
    comment
  }
`);

const insertFeedbackMutation = graphql(
  `
    mutation insertSummaryFeedback(
      $appointmentId: uuid!
      $type: FeedbackTypeEnum!
      $llmOutputId: uuid
    ) {
      insertSummaryFeedbackOne(
        object: {
          appointmentId: $appointmentId
          type: $type
          llmOutputId: $llmOutputId
        }
      ) {
        ...FeedbackFragment
      }
    }
  `,
  [feedbackFragment],
);

const updateFeedbackTypeMutation = graphql(
  `
    mutation updateSummaryFeedback(
      $id: uuid!
      $tenantId: uuid!
      $type: FeedbackTypeEnum
    ) {
      updateSummaryFeedbackByPk(
        pkColumns: { id: $id, tenantId: $tenantId }
        _set: { type: $type }
      ) {
        ...FeedbackFragment
      }
    }
  `,
  [feedbackFragment],
);

const updateFeedbackCommentMutation = graphql(
  `
    mutation updateSummaryFeedback(
      $id: uuid!
      $tenantId: uuid!
      $comment: String
    ) {
      updateSummaryFeedbackByPk(
        pkColumns: { id: $id, tenantId: $tenantId }
        _set: { comment: $comment }
      ) {
        ...FeedbackFragment
      }
    }
  `,
  [feedbackFragment],
);

interface InlineFeedbackBoxProps {
  appointmentId: string;
  llmOutputId: string;
  sectionLabel: string;
}

export const InlineFeedbackBox: React.FC<InlineFeedbackBoxProps> = ({
  appointmentId,
  llmOutputId,
  sectionLabel,
}) => {
  const toast = useToast();
  const tenantId = useAssignedTenantIdFromParams();
  const apiClient = useApiClient();
  const [feedbackId, setFeedbackId] = useState<string | null>(null);
  const [feedbackType, setFeedbackType] = useState<FeedbackTypeEnum | null>(
    null,
  );
  const [feedbackScoreId, setFeedbackScoreId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isFinalized, setIsFinalized] = useState<boolean>(false);
  const isReadonly = useIsAppointmentReadonly();

  const [insertFeedback] = useMutation(insertFeedbackMutation, {
    onCompleted: (data) => {
      const newId = data?.insertSummaryFeedbackOne?.id;
      if (newId) {
        setFeedbackId(newId);
      }
    },
    optimisticResponse: ({ type }) => ({
      insertSummaryFeedbackOne: {
        __typename: 'SummaryFeedback' as const,
        id: `temp-feedback-${v4()}`,
        type: type,
        comment: null,
      },
    }),
    onError: () => toast.error('Failed to send feedback'),
  });

  const [updateFeedbackType] = useMutation(updateFeedbackTypeMutation, {
    optimisticResponse: ({ id, type }) => ({
      updateSummaryFeedbackByPk: {
        __typename: 'SummaryFeedback' as const,
        id,
        type: type ?? null,
        comment: null,
      },
    }),
    onError: () => toast.error('Failed to update feedback'),
  });

  const [updateFeedbackComment] = useMutation(updateFeedbackCommentMutation, {
    optimisticResponse: ({ id, comment }) => ({
      updateSummaryFeedbackByPk: {
        __typename: 'SummaryFeedback' as const,
        id,
        type: feedbackType ?? null,
        comment: comment ?? null,
      },
    }),
    onError: () => toast.error('Failed to update feedback comment'),
  });

  const handleThumbsClick = async (type: FeedbackTypeEnum) => {
    if (isFinalized) {
      return;
    }

    setIsSubmitting(true);

    if (feedbackId) {
      await updateFeedbackType({
        variables: { id: feedbackId, tenantId, type },
      });
      setFeedbackType(type);
    } else {
      const result = await insertFeedback({
        variables: { appointmentId, llmOutputId, type },
      });
      const newId = result?.data?.insertSummaryFeedbackOne?.id;
      if (newId) {
        setFeedbackId(newId);
        setFeedbackType(type);
      }
    }

    if (feedbackScoreId) {
      await apiClient.llmOutputs.updateUserFeedbackScore({
        params: { tenantId, llmOutputId, scoreId: feedbackScoreId },
        body: { value: type },
      });
    } else {
      const result = await apiClient.llmOutputs.addUserFeedbackScore({
        params: { tenantId, llmOutputId },
        body: { value: type },
      });
      if (result.status === 201) {
        setFeedbackScoreId(result.body.scoreId);
      }
    }

    setIsSubmitting(false);
  };

  const handleCommentSubmit = async (comment: string) => {
    if (!feedbackId || !feedbackType || isFinalized) {
      return;
    }

    setIsSubmitting(true);

    await updateFeedbackComment({
      variables: { id: feedbackId, tenantId, comment },
    });

    if (feedbackScoreId) {
      await apiClient.llmOutputs.updateUserFeedbackScore({
        params: { tenantId, llmOutputId, scoreId: feedbackScoreId },
        body: {
          value: feedbackType,
          comment,
        },
      });
    }

    const message =
      feedbackType === 'POSITIVE'
        ? 'Thanks for the feedback! Glad you liked this one!'
        : 'Sorry that this summary missed the mark. Every piece of feedback you give (positive or negative) helps us improve our future results.';

    toast.success(message);

    setIsFinalized(true);
    setIsSubmitting(false);
  };

  const handleClose = () => {
    if (isFinalized) {
      return;
    }
    setIsFinalized(true);
  };

  return (
    !isReadonly && (
      <InlineFeedback
        sectionLabel={sectionLabel}
        feedbackType={feedbackType}
        isSubmitting={isSubmitting}
        isFinalized={isFinalized}
        onThumbsClick={handleThumbsClick}
        onCommentSubmit={handleCommentSubmit}
        onClose={handleClose}
      />
    )
  );
};
